import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import { useMoveNode } from "@/tiptap/ui/move-node-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function MoveNodeShortcutBadge({
  shortcutKeys
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for moving a node up or down in a Tiptap editor.
 */
export const MoveNodeButton = forwardRef((
  {
    editor: providedEditor,
    text,
    direction,
    hideWhenUnavailable = false,
    onMoved,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    handleMoveNode,
    canMoveNode,
    label,
    shortcutKeys,
    Icon,
  } = useMoveNode({
    editor,
    direction,
    hideWhenUnavailable,
    onMoved,
  })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleMoveNode()
  }, [handleMoveNode, onClick])

  if (!isVisible) {
    return null
  }

  const tooltip = direction === "up" ? "Move Up" : "Move Down"

  return (
    <Button
      type="button"
      variant="ghost"
      role="button"
      tabIndex={-1}
      aria-label={label}
      tooltip={tooltip}
      disabled={!canMoveNode}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <MoveNodeShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

MoveNodeButton.displayName = "MoveNodeButton"
